import { capitalize, Tooltip } from '@mui/material';
import { type SupportedColorScheme } from '@mui/material/styles';
import { type SystemStyleObject } from '@mui/system';
import { type ClusterValid } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { BoxIcon, CpuIcon, GpuIcon, HardDriveIcon, MemoryStickIcon } from 'lucide-react';
import { type ClusterNodeResourcesDto } from '@/api/services/cluster';
import {
  type ListGlobalCloudProviderDto,
  type ListGlobalCloudProviderRegionsDto,
  type ListGlobalCloudProvidersDto,
} from '@/api/services/platform';
import { type ClusterResourceType } from '@/utils/booking-utils';
import { getRandomElement } from '@/utils/utils';
import { ImageThemed } from '../Common/ImageThemed';
import { Icon } from '../Icon/Icon';
import { fromMillicoresToDecimal } from './ClusterSetup/helpers/fromMillicoresToDecimal';
import { CLOUD_PROVIDER_IMGS, CLOUD_PROVIDER_MAP, type CloudProvider, K8S_DISTRIBUTION_IMGS } from './constants';
import { formatGib } from './shared/ClusterSummary/utils/formatGib';

type ByteUnitStandard = 'IEC' | 'SI';
/**
 * Format bytes as human-readable text.
 *
 * According to the SI standard, there are 1000 bytes in a kilobyte.
 * There is another standard called IEC that has 1024 bytes in a kibibyte,
 * but this is only useful when measuring things that are naturally a power of two, e.g. a stick of RAM.
 */
export type ByteSizeFormatterOptions = {
  standard?: ByteUnitStandard;
  decimalPlaces?: number;
};

type FormatResourceOptionsReturn = [string, string];

export function formatResourcesOptions(
  amount: number,
  type?: ClusterResourceType,
  nodes = 1,
): FormatResourceOptionsReturn {
  let tuple: FormatResourceOptionsReturn = ['', ''];

  switch (type) {
    case 'cpu':
      tuple = ['vCPUs', fromMillicoresToDecimal(amount)];
      break;
    case 'gpu':
      tuple = ['GPUs', fromMillicoresToDecimal(amount)];
      break;
    case 'ram':
      tuple = [type.toUpperCase(), formatGib(amount)];
      break;
    case 'disk':
      tuple = [capitalize(type), formatGib(amount)];
      break;
    default:
      throw new Error('Invalid resource option type');
  }

  if (nodes > 1) {
    const formattedAmount = tuple[1];
    const formattedTotal =
      type === 'cpu' || type === 'gpu' ? fromMillicoresToDecimal(amount * nodes) : formatGib(amount * nodes);
    tuple[1] = `${formattedAmount} x ${nodes} = ${formattedTotal}`;
  }

  return tuple;
}

export function getResourceIcon(
  type?: ClusterResourceType | 'nodes',
  color: 'secondary.main' | 'info.main' = 'secondary.main',
  size = 16,
) {
  switch (type) {
    case 'cpu':
      return <Icon icon={CpuIcon} size={size} sx={{ color }} />;
    case 'gpu':
      return <Icon icon={GpuIcon} size={size} sx={{ color }} />;
    case 'ram':
      return <Icon icon={MemoryStickIcon} size={size} sx={{ color }} />;
    case 'disk':
      return <Icon icon={HardDriveIcon} size={size} sx={{ color }} />;
    case 'nodes':
      return <Icon icon={BoxIcon} size={size} sx={{ color }} />;
    default:
      throw new Error('Invalid resource type');
  }
}

const imageStyle: SystemStyleObject = {
  width: 24,
  height: 24,
  aspectRatio: '1/1',
};

/**
 * Returns img jsx element with cloud provider logo adapted to the theme mode change
 */
export function getProviderImage(cloudProvider: CloudProvider, colorScheme: SupportedColorScheme) {
  const imgData = CLOUD_PROVIDER_IMGS[cloudProvider];

  // A backup records whatever provider the API sent; one without a logo here renders no icon.
  if (!imgData) {
    return null;
  }

  return (
    <Tooltip title={imgData.tooltip}>
      <ImageThemed
        src={imgData.src}
        themed={imgData.themed}
        colorScheme={colorScheme}
        alt={cloudProvider}
        style={imageStyle}
      />
    </Tooltip>
  );
}

export function getK8sDistributionImage(
  k8sDistribution: string,
  colorScheme: SupportedColorScheme,
  errorCallback?: () => void,
) {
  const imgData = K8S_DISTRIBUTION_IMGS[k8sDistribution];

  if (!imgData?.src) {
    return null;
  }

  // If the image is not themed, it is a string (theme agnostic) or an object with a string for each theme
  const imgSource = typeof imgData.src === 'string' ? imgData.src : imgData.src[colorScheme];

  return (
    <Tooltip title={`${CLOUD_PROVIDER_IMGS[CLOUD_PROVIDER_MAP.HYBRID].tooltip} on ${imgData.tooltip}`}>
      <ImageThemed
        src={imgSource}
        themed={imgData.themed}
        colorScheme={colorScheme}
        alt={k8sDistribution}
        style={imageStyle}
        onError={errorCallback}
      />
    </Tooltip>
  );
}

/**
 * Makes sure the number is not too big to be represented as a JavaScript number.
 */
const checkForSafeNumber = (value: number) => {
  if (value > Number.MAX_SAFE_INTEGER) {
    throw new RangeError('The number is too big to be represented as a JavaScript number.');
  }
};

export function formatBytesToGbytes(
  bytes: number,
  { standard = 'IEC', decimalPlaces = 1 }: ByteSizeFormatterOptions = {},
) {
  checkForSafeNumber(bytes);
  const converter = standard === 'IEC' ? transformBytesToGibibytes : transformBytesToGigabytes;
  const suffix = standard === 'IEC' ? 'GiB' : 'GB';

  const value = converter(bytes).toFixed(decimalPlaces);

  return `${value} ${suffix}`;
}

/**
 * Transforms gigabytes to bytes. (1 Gigabyte = 1000^3 bytes, different from Gibibyte = 1024^3 bytes)
 */
export function transformGigabytesToBytes(gigabytes = 0) {
  return gigabytes * 1000 ** 3;
}

/**
 * Transforms bytes to gigabytes. (1.000.000.000 bytes = 1 Gigabyte = 1 GB),
 * different from 1 GiB = 1.073.741.824 bytes)
 */
export function transformBytesToGigabytes(bytes = 0) {
  return bytes / 1000 ** 3;
}

/**
 * Transforms gibibytes to bytes. (1 Gibibyte = 1024^3 bytes)
 */
export function transformGibibytesToBytes(gigabytes = 0) {
  return gigabytes * 1024 ** 3;
}

/**
 * Transforms bytes to gibibytes. (1.073.741.824 bytes = 1 Gibibyte = 1 GiB),
 */
export function transformBytesToGibibytes(bytes = 0) {
  return bytes / 1024 ** 3;
}

/**
 * Returns the total amount of a resource by adding up base, extra & complimentary.
 */
export function getNodeResourceTotal(resource?: ClusterNodeResourcesDto | null): number {
  if (!resource) {
    return 0;
  }

  const getResource = (resourceType: keyof ClusterNodeResourcesDto): number => resource[resourceType] ?? 0;
  return getResource('base') + getResource('additional') + getResource('complimentary');
}

/**
 * Returns the cluster-wide total for a resource type: per-node amount × number of nodes.
 */
export function getClusterResourceTotal(cluster: ClusterValid, type: 'disk' | 'ram' | 'cpu' | 'gpu'): number {
  return getNodeResourceTotal(cluster.state?.resources?.[type] ?? null) * (cluster.configuration.numberOfNodes ?? 1);
}

/**
 * Given a float value, it will format it to the amount of digits passed as `fixedTo`.
 * and will add a suffix (e.g `GB`). If the value is an integer it will return it without decimals (e.g `2 GB`).
 */
export const formatFloatWithSuffix = ({
  fixedTo,
  value = 0,
  suffix,
}: {
  fixedTo: number;
  value?: number;
  suffix?: string;
}) => {
  const readableValue = value % 1 > 0 ? parseFloat(value.toFixed(fixedTo)) : Math.trunc(value);
  let formattedValue = `${readableValue}`;
  if (suffix) {
    formattedValue += ` ${suffix}`;
  }
  return formattedValue;
};

/**
 * Select a random free tier Cloud Provider.
 */
export function getRandomCloudProvider(cloudProviders: Pick<ListGlobalCloudProviderDto, 'id'>[]): CloudProvider {
  if (cloudProviders.length === 0) {
    throw new Error('No cloud providers available');
  }
  if (cloudProviders.length === 1) {
    return cloudProviders[0].id as CloudProvider;
  }

  return getRandomElement(cloudProviders.map((item) => item.id)) as CloudProvider;
}

/** The free tier experiment's default provider; `''` means no preference. */
export type FreeTierExperimentCloudProvider = '' | 'gcp' | 'aws';

/**
 * Returns providers that are valid candidates for free-tier automatic selection.
 *
 * Selection criteria:
 * - provider is available
 * - provider supports free tier
 * - provider is not HYBRID
 *
 * HYBRID is excluded because this flow auto-selects managed cloud providers only.
 *
 * @param cloudProviders - Full list of cloud provider descriptors returned from the platform API.
 * @returns A filtered list of non-HYBRID providers eligible for free-tier auto-selection.
 */
export function getAvailableFreeTierProviders(cloudProviders: ListGlobalCloudProvidersDto) {
  return cloudProviders.filter((item) => item.available && item.freeTier && item.id !== CLOUD_PROVIDER_MAP.HYBRID);
}

/**
 * Selects the free-tier provider to use by default.
 *
 * Selection rules:
 * - If `experimentCloudProvider` is an empty string (`''`), there is no experiment preference and a
 *   random provider is selected from `freeProviders`.
 * - If `experimentCloudProvider` is set (`'aws' | 'gcp'`), that provider is selected when present
 *   in `freeProviders`.
 * - If the experiment provider is not found in `freeProviders`, the function falls back to a random
 *   provider from `freeProviders`.
 *
 * @param freeProviders - Available free-tier providers eligible for selection.
 * @param experimentCloudProvider - Experiment-selected default provider, or `''` when unrestricted.
 * @returns The selected provider id.
 */
export function selectFreeTierProvider(
  freeProviders: ListGlobalCloudProvidersDto,
  experimentCloudProvider: FreeTierExperimentCloudProvider,
): CloudProvider {
  // Empty experiment means "no preference": pick a random free-tier provider.
  if (experimentCloudProvider === '') {
    return getRandomCloudProvider(freeProviders);
  }

  return (
    (freeProviders.find((item) => item.id === experimentCloudProvider)?.id as CloudProvider | undefined) ??
    getRandomCloudProvider(freeProviders)
  );
}

/**
 * Finds, based on a free tier Cloud Provider, a random region that
 * is selected from a random free tier-allowed geography region.
 */
export function getRandomCloudRegion(cloudProviderRegions: ListGlobalCloudProviderRegionsDto) {
  // Select a random geography from the ones available for the free tier for that provider
  const randomGeography = getRandomElement([
    ...new Set(cloudProviderRegions.map(({ geographicalSubRegion }) => geographicalSubRegion)),
  ]);
  // Select a random region from the selected geography
  const regions = cloudProviderRegions.filter(({ geographicalSubRegion }) => geographicalSubRegion === randomGeography);
  if (regions.length === 1) {
    return regions[0].id;
  }

  return getRandomElement(regions)?.id;
}

/**
 * Selects a default region for the given cloud provider regions.
 * If a pre-selected region is provided and it belongs to the selected provider, it will be used.
 * Otherwise, a random one from the results.
 */
export function selectDefaultRegion({
  cloudProviderRegions,
  preSelectedRegion,
}: {
  cloudProviderRegions: ListGlobalCloudProviderRegionsDto;
  preSelectedRegion?: string;
}): string | undefined {
  if (preSelectedRegion && cloudProviderRegions.some((region) => region.id === preSelectedRegion)) {
    return preSelectedRegion;
  }
  return getRandomCloudRegion(cloudProviderRegions);
}
